import { useState } from 'react'
import { api } from '../../services/api.js'
import PartialDateInput from '../shared/PartialDateInput.jsx'

const FIELDS = [
  { value: 'first_name',     label: 'All given names' },
  { value: 'preferred_name', label: 'Preferred name' },
  { value: 'last_name',      label: 'Last name' },
  { value: 'birth_name',     label: 'Birth name' },
  { value: 'gender',         label: 'Gender' },
  { value: 'birth_date',     label: 'Birth date' },
  { value: 'birth_place',    label: 'Birth place' },
  { value: 'death_date',     label: 'Death date' },
  { value: 'death_place',    label: 'Death place' },
  { value: 'occupation',     label: 'Occupation' },
  { value: 'notes',          label: 'Notes' },
]

export default function SuggestChangeModal({ person, treeId, onClose }) {
  const [field, setField] = useState('first_name')
  const [value, setValue] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(null)

  const currentValue = person[field] || ''

  async function submit(e) {
    e.preventDefault()
    if (!value.trim()) return setError('Please enter a suggested value.')
    setSaving(true)
    setError(null)
    try {
      await api.post('/suggestions', {
        tree_id: treeId,
        person_id: person.id,
        field,
        suggested_value: value.trim(),
        note: note.trim() || null,
      })
      setDone(true)
    } catch (err) {
      setError(err.message || 'Failed to submit suggestion')
    } finally {
      setSaving(false)
    }
  }

  const personName = person.preferred_name || person.first_name || 'this person'

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()} role="dialog">
        <div className="modal__header">
          <div className="modal__title">Suggest a change</div>
          <button className="modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="modal__body">
          {done ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✓</div>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Suggestion submitted</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                The tree owner will be notified and can approve or reject your suggestion.
              </div>
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
                Suggest a correction or addition for {personName}. The tree owner will review it before any change is applied.
              </p>

              <div className="form-field" style={{ marginBottom: '1rem' }}>
                <label className="form-field__label">Field</label>
                <select className="form-input form-input--select" value={field}
                  onChange={e => { setField(e.target.value); setValue('') }}>
                  {FIELDS.map(f => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>

              {currentValue && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem',
                  background: 'var(--surface)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
                  Current value: <strong>{currentValue}</strong>
                </div>
              )}

              <div className="form-field" style={{ marginBottom: '1rem' }}>
                <label className="form-field__label">Suggested value</label>
                {field === 'birth_date' || field === 'death_date' ? (
                  <PartialDateInput value={value} onChange={setValue} />
                ) : (
                  <input
                    className="form-input"
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    placeholder={`Enter the correct ${FIELDS.find(f => f.value === field)?.label.toLowerCase()}`}
                  />
                )}
              </div>

              <div className="form-field" style={{ marginBottom: '1.25rem' }}>
                <label className="form-field__label">Note (optional)</label>
                <textarea
                  className="form-input form-input--textarea"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Source, reason for the change, etc."
                  rows={3}
                />
              </div>

              {error && <div className="form-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Submitting…' : 'Submit suggestion'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
